import { SolutionScenario } from '@/types/scenarios/solution';
import { Wrench, Phone, HelpCircle, Truck, FileText, Package, Calendar, ShieldCheck } from 'lucide-react';

export const equipment: SolutionScenario = {
  meta: {
    id: 'equipment',
    slug: 'equipment',
    name: 'Equipment',
    tagline: 'Genies that replace the manual and run the help desk',
    description: 'A genie that walks customers through how the gear works, handles damage and breakdowns, books returns and extensions, and answers the same operating questions a hundred times a day so your counter team stays free.',
    market: 'Equipment',
    subMarkets: ['Equipment Hire', 'Tool Rental', 'Construction Equipment', 'Party & Event Rental', 'Heavy Machinery Rental', 'Equipment Dealers'],
    specificNeeds: [
      'How-to and operating support for rented equipment',
      'Damage and incident reporting',
      'Pickup, return, and extension scheduling',
      'After hours breakdown support',
      'Booking availability and quoting',
      'Account, invoice, and deposit questions',
    ],
    commonRoles: ['Yard Staff', 'Service Coordinators', 'Counter Staff', 'Account Managers'],
    marketSize: 'NZ$1.5B+ AU$10B+ rental and hire market',
    isFeatured: true,
  },

  hero: {
    headline: 'Replace the manual, not the people',
    headlineGradient: 'Replace the manual,',
    headlineRest: 'not the people',
    subheadline: "A genie that walks your customers through how the gear works, handles breakdowns and damage in the moment, and answers the same operating questions a hundred times a day so your counter team can serve the people in front of them.",
  },

  recommendedPlaybooks: [
    'equipment-troubleshooting',
    'interactive-faq',
    'appointment-scheduler',
    'lead-capture',
  ],

  applications: [
    {
      id: 'how-to-support',
      name: 'How-To & Operating Support',
      description: 'Customers call mid-job asking how to use the equipment. Your Genie walks them through it instead of pulling staff off the counter.',
      department: 'Support',
      icon: HelpCircle,
      playbookIds: ['equipment-troubleshooting', 'interactive-faq'],
      benefits: [
        'Answer the same setup questions every day',
        'Walk through start-up and shut-down procedures',
        'Brand and model-specific guidance',
        'Cut interruptions to your counter team',
      ],
    },
    {
      id: 'damage-reporting',
      name: 'Damage & Incident Reporting',
      description: 'Capture the story, the photos, and the details the moment something happens, not three days later when memory has faded.',
      department: 'Operations',
      icon: ShieldCheck,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Capture incident details in the moment',
        'Trigger photo and video uploads',
        'Route serious incidents to a manager',
        'Cleaner insurance and damage claims',
      ],
    },
    {
      id: 'returns-pickups',
      name: 'Returns, Pickups & Extensions',
      description: 'Customers want to extend, swap, or arrange a pickup. Your Genie books it without anyone touching the phone.',
      department: 'Operations',
      icon: Truck,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Schedule pickups and drop-offs',
        'Handle hire extensions on the spot',
        'Confirm return windows and locations',
        'Reduce no-show and overdue rentals',
      ],
    },
    {
      id: 'breakdown-support',
      name: 'After Hours Breakdown Support',
      description: 'A skid steer stops working at 6 PM on a Saturday. Your Genie triages and either fixes it on the call or escalates to on call.',
      department: 'Support',
      icon: Wrench,
      playbookIds: ['equipment-troubleshooting'],
      benefits: [
        '24/7 front line breakdown support',
        'Guided troubleshooting before dispatch',
        'Smart escalation to on call staff',
        'Customer kept productive longer',
      ],
    },
    {
      id: 'booking-quoting',
      name: 'Bookings & Availability',
      description: 'Quote a four-day excavator hire, check what is on the yard, and book it. Even at 9 PM the night before the job starts.',
      department: 'Sales',
      icon: Phone,
      playbookIds: ['lead-capture', 'appointment-scheduler'],
      benefits: [
        'After hours booking capture',
        'Availability checks across the fleet',
        'Quote requests with delivery options',
        'Hand off to staff with full context',
      ],
    },
    {
      id: 'account-billing',
      name: 'Account & Invoice Questions',
      description: 'Routine billing, deposit, and account queries handled without pulling the bookkeeper off the books.',
      department: 'Account Management',
      icon: FileText,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Answer common invoice questions',
        'Explain deposit and bond policies',
        'Account credit and limit lookups',
        'Cleaner end-of-month for your team',
      ],
    },
  ],

  challenges: [
    {
      title: 'Same Questions, All Day Every Day',
      description: 'Counter and yard staff lose hours a day walking customers through the same operating instructions, fuel types, and start-up procedures.',
      impact: 'high',
      affectedAreas: ['Counter productivity', 'Yard throughput', 'Staff morale'],
    },
    {
      title: 'After Hours Breakdowns',
      description: 'Equipment fails on a Saturday job site and the customer cannot reach anyone. They escalate, leave bad reviews, or do not book again.',
      impact: 'high',
      affectedAreas: ['Customer retention', 'Reputation', 'Repeat bookings'],
    },
    {
      title: 'Late or Lost Damage Information',
      description: 'Damage gets reported days after it happened, photos are missing, and the story changes. Insurance claims get messy and so do invoices.',
      impact: 'medium',
      affectedAreas: ['Insurance recovery', 'Customer disputes', 'Margin'],
    },
    {
      title: 'Booking Drop-Off After Hours',
      description: 'Tradies and project managers plan jobs in the evening. If you only take bookings 8-5, you miss the moment they are ready to commit.',
      impact: 'medium',
      affectedAreas: ['Revenue', 'Utilization', 'Competitive positioning'],
    },
  ],

  solutions: [
    {
      challenge: 'Counter staff drowning in repeat support calls',
      solution: 'Voice Genie handles front line support and how-to questions',
      implementation: 'Train the Genie on your fleet, common questions, and operating procedures. Calls deflect before they hit the counter.',
      expectedResults: [
        '50% reduction in routine support calls',
        'Counter team back to serving walk-ins',
        'Faster yard turnarounds',
      ],
    },
    {
      challenge: 'No coverage for after hours breakdowns',
      solution: '24/7 Voice Genie with smart escalation',
      implementation: 'Always on troubleshooting that resolves what it can and pulls on call staff in only when needed.',
      expectedResults: [
        'Round-the-clock customer coverage',
        '40%+ of issues resolved without callout',
        'Better reviews and repeat bookings',
      ],
    },
    {
      challenge: 'Bookings and quotes lost after hours',
      solution: 'Voice Genie captures bookings and quote requests overnight',
      implementation: 'Genie checks availability, captures the job details, and queues it for the team to confirm in the morning.',
      expectedResults: [
        'No more "I called and got voicemail"',
        'Higher fleet utilization',
        'More returning customers',
      ],
    },
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'Point of Rental',
        category: 'Rental Management',
        description: 'Equipment rental management platform',
        required: false,
        popularityScore: 88,
      },
      {
        name: 'Texada',
        category: 'Rental Management',
        description: 'Heavy equipment rental and dealer software',
        required: false,
        popularityScore: 85,
      },
      {
        name: 'InspHire',
        category: 'Rental Management',
        description: 'Hire and rental management software',
        required: false,
        popularityScore: 80,
      },
      {
        name: 'BoomRentals',
        category: 'Rental Management',
        description: 'Rental booking and fleet management',
        required: false,
        popularityScore: 70,
      },
    ],
    common: [
      {
        name: 'Xero',
        category: 'Accounting',
        description: 'Cloud accounting and invoicing',
        required: false,
        popularityScore: 92,
      },
      {
        name: 'HubSpot',
        category: 'CRM',
        description: 'Customer relationship management',
        required: false,
        popularityScore: 88,
      },
    ],
  },

  metrics: {
    averageImplementationTime: '1-2 weeks',
    typicalROI: '310%',
    adoptionRate: '85% customer satisfaction',
    customerSatisfaction: '4.6/5',
  },

  demoConfig: {
    firstMessage: "Hey, I can help you with how to use a piece of gear, book a pickup or extension, report damage, or check availability. What do you need?",
    systemPrompt: "You are a knowledgeable equipment hire support assistant. Help customers operate hired equipment, walk them through start-up and shut-down procedures, handle damage and incident reports, schedule pickups and returns, and capture booking enquiries. Be practical, calm, and concise. You know the fleet inside out and you talk like someone who has worked the counter for years.",
    marketContext: "Equipment hire, rental, or dealer business handling support, bookings, returns, and incidents",
    exampleScenarios: [
      "How do I start the skid steer I picked up this morning",
      "I need to extend my excavator hire by two days",
      "The generator we hired has stopped working",
      "I knocked the side panel of the boom lift, what do I do",
    ],
  },

  caseStudies: [
    {
      companyName: 'South Coast Hire',
      companySize: '3 yards, 22 staff',
      location: 'New Zealand',
      challenge: 'Counter staff pulled off walk-ins by repeat phone questions about operating gear, especially during peak hire windows. After hours breakdowns were going to voicemail.',
      implementation: 'Deployed Help Genie for front line support, breakdown triage, and after hours booking capture across all three yards.',
      timeline: '2 weeks',
      results: [
        {
          metric: 'Repeat Support Calls',
          value: '-48%',
          improvement: 'Counter team freed up',
        },
        {
          metric: 'After Hours Bookings',
          value: '+27%',
          improvement: 'Captured overnight enquiries',
        },
        {
          metric: 'Breakdown Resolution',
          value: '41%',
          improvement: 'Solved without callout',
        },
        {
          metric: 'Customer Wait Time',
          value: '< 30 sec',
          improvement: 'From 3+ minutes peak',
        },
      ],
      quote: {
        text: "Our staff used to spend half their day explaining how to start a skid steer. The Genie does it now and our team is actually serving people who walk into the yard.",
        author: 'Mark Reilly',
        title: 'Operations Manager',
      },
    },
  ],

  resources: [
    {
      type: 'guide',
      title: 'Equipment Hire Voice Genie Guide',
      description: 'Implementing voice support for hire and rental businesses',
      url: '/resources/equipment-voice-guide',
    },
    {
      type: 'webinar',
      title: 'Cutting Support Calls in Half at the Hire Counter',
      description: 'How hire businesses are deflecting routine calls and freeing staff',
      url: '/webinars/equipment-support',
    },
    {
      type: 'template',
      title: 'Equipment How-To Scripts',
      description: 'Pre-built voice scripts for common operating questions',
      url: '/templates/equipment-how-to',
    },
  ],

  faqs: [
    {
      question: 'Can the Genie handle questions about specific brands and models?',
      answer: 'Yes. Help Genie can be trained on your specific fleet, including brand and model-level start-up procedures, fuel and lubricant requirements, common error indicators, and safety steps. Customers get the same answers your best counter staff would give.',
    },
    {
      question: 'How does it handle damage reports and incidents?',
      answer: 'The Genie walks the caller through what happened, captures the key details, prompts for photos or video uploads, and routes serious incidents to a manager. The full record lands in your system before the gear is even back on the truck.',
    },
    {
      question: 'Will it integrate with our rental management software?',
      answer: 'Yes. Help Genie connects with rental and hire platforms like Point of Rental, Texada, and InspHire, plus accounting and CRM tools, so bookings, extensions, and returns flow into the systems you already use.',
    },
  ],

  pageContent: {
    scenariosHeader: {
      title: "The same question,\nfor the hundredth time today",
      subtitle: "See how your Genie handles the calls your counter team gets every single day, so they can serve the people standing in front of them.",
    },
    chatScenarios: [
      {
        id: 'how-to',
        title: 'How Do I Start This Thing',
        messages: [
          { type: 'user' as const, text: "Hey, I picked up the skid steer this morning and I cannot get it to start. What am I doing wrong?", delay: 300 },
          { type: 'genie' as const, text: "Happy to help. Skid steers have a safety interlock, so a few things have to be in place before it cranks. Are you sat in the seat with the seatbelt on, and is the safety bar lowered?", delay: 1200 },
          { type: 'user' as const, text: "Yeah I'm in the seat... oh, the bar is up. Hang on.", delay: 2500 },
          { type: 'genie' as const, text: "That will be it. Lower the bar all the way down, foot on the brake, then turn the key. It should fire up.", delay: 3400 },
          { type: 'user' as const, text: "Okay it started, thanks!", delay: 4600 },
          { type: 'genie' as const, text: "Easy. Let it warm up for a minute before you load it up. Anything else before you get into the job?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Operating question answered', delay: 6500 },
          { id: '2', text: 'Customer back on the job in 90 seconds', delay: 6700 },
          { id: '3', text: 'No counter staff pulled off walk-ins', delay: 6900 },
          { id: '4', text: 'Customer trust built', delay: 7100 },
          { id: '5', text: 'Call deflected from peak window', delay: 7300 },
        ],
      },
      {
        id: 'breakdown',
        title: 'After Hours Breakdown',
        messages: [
          { type: 'user' as const, text: "It's Saturday afternoon and the generator we hired has just died. We need it for the function tonight.", delay: 300 },
          { type: 'genie' as const, text: "Right, let's get you sorted. First quick check, is the fuel tank above a quarter, and did the low oil light come on at all before it cut out?", delay: 1200 },
          { type: 'user' as const, text: "Fuel is fine, no light I noticed.", delay: 2500 },
          { type: 'genie' as const, text: "Okay. There's a reset switch on the side panel, near the breaker. Hold it for five seconds, then try to start her up.", delay: 3400 },
          { type: 'user' as const, text: "Hang on... yeah she's running again!", delay: 4600 },
          { type: 'genie' as const, text: "Sweet. If it goes again, give us a call and I'll get on call out to you straight away. I've logged this in your hire record either way.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Breakdown triaged after hours', delay: 6500 },
          { id: '2', text: 'Issue resolved without callout', delay: 6700 },
          { id: '3', text: 'Function saved, customer kept', delay: 6900 },
          { id: '4', text: 'Hire record updated automatically', delay: 7100 },
          { id: '5', text: 'On call staff stayed home', delay: 7300 },
        ],
      },
      {
        id: 'extension',
        title: 'Hire Extension',
        messages: [
          { type: 'user' as const, text: "I've got the excavator out til Wednesday but the job is running long. Can I keep it til Friday?", delay: 300 },
          { type: 'genie' as const, text: "Yep, let me check the schedule. Can I grab the hire reference or the name on the booking?", delay: 1200 },
          { type: 'user' as const, text: "It's under Daniel Pope, hire ref 88241.", delay: 2500 },
          { type: 'genie' as const, text: "Got you, Daniel. The 5-tonne excavator is free through Friday, no problem. I'll extend the hire to Friday 5 PM and update the bond and invoice. Same drop-off, or do you need a pickup arranged?", delay: 3400 },
          { type: 'user' as const, text: "Could you arrange the pickup actually, end of day Friday?", delay: 4600 },
          { type: 'genie' as const, text: "Done. Pickup booked for Friday after 4 PM. You'll get a confirmation by text. Anything else before I let you go?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Hire extension booked', delay: 6500 },
          { id: '2', text: 'Availability checked live', delay: 6700 },
          { id: '3', text: 'Pickup scheduled', delay: 6900 },
          { id: '4', text: 'Invoice and bond updated', delay: 7100 },
          { id: '5', text: 'Confirmation sent automatically', delay: 7300 },
        ],
      },
      {
        id: 'damage',
        title: 'Damage Report',
        messages: [
          { type: 'user' as const, text: "I clipped the side panel of the boom lift on a fence post. It's a decent dent.", delay: 300 },
          { type: 'genie' as const, text: "Thanks for telling us straight away, that always makes things easier. Is the lift still operational, and is anyone hurt?", delay: 1200 },
          { type: 'user' as const, text: "All good, just the panel.", delay: 2500 },
          { type: 'genie' as const, text: "Good to hear. I've started an incident on your hire record. Could you take three photos: a wide shot, a close-up of the damage, and one of the surrounding area, then text them to the number that just came through?", delay: 3400 },
          { type: 'user' as const, text: "Photos sent.", delay: 4600 },
          { type: 'genie' as const, text: "Got them, attached to your incident. A team member will follow up about repair and any cost before you return the lift. Carry on with the job for now.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Damage captured in real time', delay: 6500 },
          { id: '2', text: 'Photos attached to hire record', delay: 6700 },
          { id: '3', text: 'Manager follow-up queued', delay: 6900 },
          { id: '4', text: 'Cleaner insurance claim', delay: 7100 },
          { id: '5', text: 'Customer relationship preserved', delay: 7300 },
        ],
      },
      {
        id: 'booking',
        title: 'After Hours Booking',
        messages: [
          { type: 'user' as const, text: "I need a 3-tonne digger for next Monday through Thursday. Got anything?", delay: 300 },
          { type: 'genie' as const, text: "Let me check. Yep, we've got a 3-tonne available across that whole window. Is it for a residential or commercial job, and do you need delivery or are you collecting?", delay: 1200 },
          { type: 'user' as const, text: "Residential, and delivery please.", delay: 2500 },
          { type: 'genie' as const, text: "Easy. Can I grab the delivery address, your name, and a contact number?", delay: 3400 },
          { type: 'user' as const, text: "Sam Lawson, 027 555 4910, 14 Awa Road in Onehunga.", delay: 4600 },
          { type: 'genie' as const, text: "Got it Sam. I've held the digger for you Monday to Thursday with delivery to 14 Awa Road. Our team will confirm the quote and delivery slot first thing in the morning. You'll get it by text and email.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'After hours booking captured', delay: 6500 },
          { id: '2', text: 'Availability confirmed live', delay: 6700 },
          { id: '3', text: 'Customer details collected', delay: 6900 },
          { id: '4', text: 'Held in system for team confirmation', delay: 7100 },
          { id: '5', text: 'Booking that would have been lost, saved', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Run the Help Desk Without the Help Desk",
      subtitle: "Your counter team should be serving people, not answering the same operating question 30 times a day. Your Genie does the repeat work so they can.",
    },
    useCaseCards: [
      {
        id: 'how-to-support',
        title: 'How-To & Operating Support',
        description: "Customers call mid-job asking how the gear works. Your Genie talks them through it the way your best counter staff would.",
        bullets: [
          'Brand and model-specific operating guidance',
          'Start-up, shut-down, and safety steps',
          'Cut routine support calls in half',
          'Free your team for walk-ins and yard work',
        ],
        image: '/solutions-pages/equipment/usecase-how-to.webp',
      },
      {
        id: 'breakdown-support',
        title: 'After Hours Breakdown Triage',
        description: "When equipment stops on a Saturday afternoon, your Genie gets it going again or pulls in on call only when it has to.",
        bullets: [
          '24/7 front line breakdown support',
          'Guided troubleshooting before any callout',
          'Smart escalation to on call staff',
          'Better reviews, more repeat bookings',
        ],
        image: '/solutions-pages/equipment/usecase-breakdown.webp',
      },
      {
        id: 'returns-extensions',
        title: 'Returns, Pickups & Extensions',
        description: "Hire extensions, pickups, and swap-outs all booked over the phone without anyone on your team picking it up.",
        bullets: [
          'Live availability checks',
          'Pickup and drop-off scheduling',
          'Hire extensions handled instantly',
          'Fewer overdue and lost rentals',
        ],
        image: '/solutions-pages/equipment/usecase-returns.webp',
      },
      {
        id: 'damage-reporting',
        title: 'Damage & Incident Capture',
        description: "Catch the story and the photos in the moment, not three days later. Your insurance claims and your customer relationships both come out cleaner.",
        bullets: [
          'In-the-moment incident capture',
          'Photo and video upload prompts',
          'Routed to a manager when serious',
          'Tighter recovery on damaged gear',
        ],
        image: '/solutions-pages/equipment/usecase-damage.webp',
      },
    ],
    finalCTA: {
      title: "Your Counter Never Closes",
      subtitle: "When a customer calls asking how to start a skid steer at 7 AM, or reports a breakdown on a Saturday, your Genie is there. Walking them through it, capturing what needs capturing, and keeping the work moving.",
      pillars: [
        {
          title: 'Always\nAnswering',
          gradient: 'from-purple-400 to-blue-400',
          description: 'The same questions handled the same way every time, day or night.',
        },
        {
          title: 'Always\nResolving',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Breakdowns triaged and fixed before anyone has to leave home.',
        },
        {
          title: 'Always\nCapturing',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Bookings, extensions, and incidents logged in real time, not the next morning.',
        },
      ],
      backgroundImage: '/solutions-pages/equipment/cta-background.webp',
    },
  },
};
